import { faPlusLarge } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StepModel, StepWithContentModel } from '@partful/instructions-domain-models';
import { classNames } from '@partful/shared/lib/classNames';
import { Surface, SurfaceClipShadow } from '@partful/shared/ui/Surface';
import { Typography } from '@partful/shared/ui/Typography';
import { ComponentProps, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './StepNavigation.module.scss';

export type StepNavigationProps = {
  steps?: (StepModel & Partial<StepWithContentModel>)[];
  activeStep?: StepModel & Partial<StepWithContentModel>;
  onChange: (step: StepModel & Partial<StepWithContentModel>) => void;
  onCreate: () => void;
  canCreate: boolean;
} & Omit<ComponentProps<typeof SurfaceClipShadow>, 'onChange'>;

export function StepNavigation({
  steps,
  activeStep,
  onChange,
  onCreate,
  canCreate,
  className,
  children,
  ...rest
}: Readonly<StepNavigationProps>) {
  const { t: i18n } = useTranslation();

  return (
    <SurfaceClipShadow shadow="large" className={classNames(styles.stepNavigation, className)} {...rest}>
      <Surface className={styles.navigation} clip="bottom" clipSize="small">
        <div className={styles.steps}>
          {steps?.map((step, index) => (
            <Fragment key={step.id}>
              <button
                type="button"
                className={styles.step}
                onClick={() => onChange(step)}
                aria-current={activeStep?.id === step.id ? 'step' : undefined}
              >
                <div>{index + 1}</div>
                <Typography appearance="label" overflow="hidden" textOverflow="ellipsis" WebkitLineClamp={3}>
                  {step.content?.name || i18n('instructions:stepBuilder.stepTitle')}
                </Typography>
              </button>
            </Fragment>
          ))}
          <button
            name="instructions_stepeditor_add_step_click"
            className={styles.addStep}
            type="button"
            onClick={onCreate}
            disabled={!canCreate}
          >
            <div>
              <FontAwesomeIcon icon={faPlusLarge} />
            </div>
            <Typography appearance="label">{i18n('instructions:stepBuilder.addStep')}</Typography>
          </button>
        </div>
      </Surface>
    </SurfaceClipShadow>
  );
}
